import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowRight, Calculator, ChevronLeft, DollarSign, Loader2 } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const SettlementPage = () => {
  const [form, setForm] = useState({
    medical_expenses: 25000,
    lost_wages: 15000,
    future_medical: 10000,
    pain_suffering_multiplier: 2.5,
    liability_percentage: 100,
    policy_limits: 100000,
    comparative_fault: 0
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const calculate = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/settlement/calculate`, form);
      setResult(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  const formatCurrency = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="settlement-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "'Fraunces', serif" }}>Settlement Calculator</h1>
          <p className="text-muted-foreground">Calculate settlement value ranges</p>
        </div>
        
        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader><CardTitle>Damages Input</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              {[
                ["medical_expenses", "Medical Expenses"],
                ["lost_wages", "Lost Wages"],
                ["future_medical", "Future Medical"],
                ["policy_limits", "Policy Limits"]
              ].map(([key, label]) => (
                <div key={key} className="space-y-1.5">
                  <Label>{label}</Label>
                  <Input 
                    type="number" 
                    value={form[key]}
                    onChange={e => setForm(p => ({ ...p, [key]: parseFloat(e.target.value) || 0 }))}
                  />
                </div>
              ))}
              
              <div className="space-y-1.5">
                <Label>Pain & Suffering Multiplier: {form.pain_suffering_multiplier}x</Label>
                <input 
                  type="range" 
                  min="1" max="5" step="0.5"
                  value={form.pain_suffering_multiplier}
                  onChange={e => setForm(p => ({ ...p, pain_suffering_multiplier: parseFloat(e.target.value) }))}
                  className="w-full"
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Comparative Fault: {form.comparative_fault}%</Label>
                <input 
                  type="range" 
                  min="0" max="50" step="5"
                  value={form.comparative_fault}
                  onChange={e => setForm(p => ({ ...p, comparative_fault: parseFloat(e.target.value) }))}
                  className="w-full"
                />
              </div>
              
              <Button className="w-full" onClick={calculate} disabled={loading}>
                {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <DollarSign className="h-4 w-4 mr-2" />}
                Calculate Value
              </Button>
            </CardContent>
          </Card>
          
          {result && (
            <Card className="animate-fade-in">
              <CardHeader><CardTitle>Settlement Analysis</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                  <p className="text-sm text-emerald-700 mb-1">Recommended Demand</p>
                  <p className="text-3xl font-bold text-emerald-700">{formatCurrency(result.recommended_demand)}</p>
                </div>
                
                <div className="p-4 bg-slate-50 rounded-xl">
                  <p className="text-sm text-muted-foreground mb-2">Settlement Range</p>
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">{formatCurrency(result.settlement_range_low)}</span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    <span className="text-lg font-semibold">{formatCurrency(result.settlement_range_high)}</span>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Economic</p>
                    <p className="font-semibold">{formatCurrency(result.economic_damages)}</p>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-lg">
                    <p className="text-xs text-muted-foreground">Non-Economic</p>
                    <p className="font-semibold">{formatCurrency(result.non_economic_damages)}</p>
                  </div>
                </div>
                
                {result.analysis_notes?.length > 0 && (
                  <div className="pt-4 border-t">
                    <p className="text-sm font-medium mb-2">Notes</p>
                    <ul className="space-y-1">
                      {result.analysis_notes.map((n, i) => (
                        <li key={i} className="text-sm text-muted-foreground">{n}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      <Footer />
    </main>
  );
};


export default SettlementPage;
